import { PropsWithChildren, useCallback, useEffect } from "react";

import { Cross1Icon } from "@radix-ui/react-icons";
import { Flex, IconButton, Dialog } from "@radix-ui/themes";
import { ContentProps } from "@radix-ui/themes/dist/cjs/components/dialog";
import { Responsive } from "@radix-ui/themes/dist/cjs/props/prop-def";
import { styled } from "@stitches/react";

const StyledDialogContent = styled(Dialog.Content, {
  position: "relative",

  variants: {
    search: {
      true: {
        overflow: "visible",
        "> div": {
          width: "100%",
          maxWidth: "100%",
        },
      },
    },
    transparent: {
      true: {
        boxShadow: "none",
        backgroundColor: "transparent",
      },
    },
    documentViewer: {
      true: {
        position: "fixed",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        maxWidth: "1280px",
        width: "100vw",
        height: "100vh",
        overflow: "hidden",
        marginTop: "var(--space-4)",
        marginBottom: "var(--space-4)",
      },
    },
  },
});

type Variants = {
  search?: boolean;
  transparent?: boolean;
  documentViewer?: boolean;
};

type PointerDownOutsideEvent = CustomEvent<{ originalEvent: PointerEvent }>;

export type Props = PropsWithChildren<{
  open: boolean;
  onClose: () => void;
  onPointerDownOutside?: ContentProps["onPointerDownOutside"];
  title?: React.ReactNode;
  size?: Responsive<"1" | "2" | "3" | "4">;
  showCloseButton?: boolean;
}> &
  Variants;

const CloseButton = styled(IconButton, {
  cursor: "pointer",
  position: "absolute",
  top: "var(--space-4)",
  right: "var(--space-4)",
});

const OUTSIDE_CLASS_LIST = ["rt-DialogScroll", "rt-DialogScrollPadding"];

export const Modal = ({
  open,
  onClose,
  title,
  size,
  showCloseButton = true,
  children,
  onPointerDownOutside,
  ...variants
}: Props) => {
  useEffect(() => {
    document.body.setAttribute("data-modal-open", String(open));
    if (open) {
      // Pushing the change to the end of the call stack
      const timer = setTimeout(() => {
        document.body.style.pointerEvents = "";
      }, 0);

      return () => clearTimeout(timer);
    } else {
      document.body.style.pointerEvents = "auto";
    }
  }, [open]);

  const defaultHandlePointerDownOutside = useCallback(
    (event: PointerDownOutsideEvent) => {
      if (
        OUTSIDE_CLASS_LIST.some((value) =>
          (event.target as Element).classList.contains(value)
        )
      ) {
        onClose();
      }
    },
    [onClose]
  );

  return (
    <Dialog.Root open={open}>
      <StyledDialogContent
        size={size}
        onPointerDownOutside={
          onPointerDownOutside ?? defaultHandlePointerDownOutside
        }
        style={{ animation: "none" }}
        onEscapeKeyDown={onClose}
        {...variants}
      >
        <Dialog.Title hidden={title === undefined}>
          <Flex justify={"start"}>{title}</Flex>
        </Dialog.Title>
        {children}
        {showCloseButton && (
          <CloseButton
            variant="ghost"
            onClick={onClose}
            style={{
              color: variants?.transparent ? "var(--gray-1)" : "var(--gray-12)",
            }}
          >
            <Cross1Icon />
          </CloseButton>
        )}
      </StyledDialogContent>
    </Dialog.Root>
  );
};
